import type { BillWithVotes, StageEntry, VoteSession } from './models'

// ---------------------------------------------------------------------------
// Date formatting
// ---------------------------------------------------------------------------

const MONTHS_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

const MONTHS_SHORT = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
]

/** Format an ISO date string (YYYY-MM-DD) to Spanish: "5 de marzo de 2026" */
export function formatDateEs(iso: string): string {
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  const day = parseInt(parts[2], 10) || 0
  const month = parseInt(parts[1], 10) || 1
  const monthName = MONTHS_ES[month - 1] ?? ''
  return `${day} de ${monthName} de ${parts[0]}`
}

/** Format an ISO date string to short Spanish: "5 Ene 2026" */
export function formatDateShort(iso: string): string {
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  const day = parseInt(parts[2], 10) || 0
  const month = parseInt(parts[1], 10) || 1
  const monthName = MONTHS_SHORT[month - 1] ?? ''
  return `${day} ${monthName} ${parts[0]}`
}

/** Build a human-readable date range from a slice of stages. */
export function dateRangeLabel(stages: Pick<StageEntry, 'date'>[]): string {
  const dates = stages.map(s => s.date).filter(d => d !== '')
  if (dates.length === 0) return ''
  const first = dates[0]
  const last = dates[dates.length - 1]
  if (first === last) {
    return formatDateShort(first)
  } else if (first.slice(0, 7) === last.slice(0, 7)) {
    // Same year-month: "5–9 Ene 2026" (en dash, no spaces)
    const dayFirst = first.split('-')[2] ?? ''
    return `${dayFirst}–${formatDateShort(last)}`
  } else {
    // Different month: "5 Ene 2026 – 9 Feb 2026" (en dash with spaces)
    return `${formatDateShort(first)} – ${formatDateShort(last)}`
  }
}

// ---------------------------------------------------------------------------
// Vote display helpers
// ---------------------------------------------------------------------------

/** Map a vote_choice string to a CSS class and display label. */
export function voteChoiceDisplay(choice: string): [string, string] {
  switch (choice) {
    case 'Si': return ['favor', 'A favor']
    case 'No': return ['contra', 'En contra']
    case 'Abstención': return ['abst', 'Abstención']
    case 'Pareo': return ['no-participa', 'Pareo']
    default: return ['no-participa', choice]
  }
}

/** Check if a vote_choice matches a filter value. */
export function voteMatchesFilter(choice: string, filter: string): boolean {
  switch (filter) {
    case 'favor': return choice === 'Si'
    case 'contra': return choice === 'No'
    case 'abst': return choice === 'Abstención'
    case 'no-participa': return choice !== 'Si' && choice !== 'No' && choice !== 'Abstención'
    default: return true
  }
}

/**
 * Classify vote results for presentation — a PURE lookup on `vote.outcome`,
 * which the CLI computes once (sync categorizeVote) and bakes into Gold.
 * No result/scope rule lives here; this avoids the historical drift where
 * ties rendered green✓ on the web but 🟡 on social/images.
 *
 * The 'amendment' yellow CSS tone is reused for ties (.vote-pill.amendment /
 * .result-badge.amendment), so ties now render yellow not-approved.
 */
export function voteResultTone(vote: VoteSession): string {
  switch (vote.outcome) {
    case 'rejected': return 'rejected'
    case 'amendment_rejected': return 'amendment'
    case 'tie': return 'amendment'
    default: return 'approved'
  }
}

/** Display label for a vote result. Pure lookup on `vote.outcome`. */
export function voteResultLabel(vote: VoteSession): string {
  if (vote.outcome === 'amendment_rejected') {
    return 'No aprobada'
  }
  // approved/rejected/tie show the raw corrected result (e.g. "Empate").
  return vote.result
}

/** Short symbol used in lifecycle vote pills and grouped summaries. Pure lookup. */
export function voteResultSymbol(vote: VoteSession): string {
  switch (vote.outcome) {
    case 'rejected': return '✗'
    case 'amendment_rejected': return '↺'
    case 'tie': return '↺'
    default: return '✓'
  }
}

// ---------------------------------------------------------------------------
// Lifecycle stage
// ---------------------------------------------------------------------------

/** Derive the current lifecycle stage as shown in the UI, plus the corresponding step anchor. */
export function currentLifecycleStage(bill: BillWithVotes): [string, string | null] {
  const hasOficio = bill.stages.some(stage => stage.description.includes('Oficio de ley al Ejecutivo'))
  const hasPublication = bill.stages.some(stage => stage.description.includes('Diario Oficial'))
  const hasLawNumber = bill.law_number != null && bill.law_number !== ''

  if (hasPublication || hasLawNumber) {
    return ['Publicado', 'lifecycle-step-ejecutivo']
  }
  if (hasOficio) {
    return ['Poder Ejecutivo', 'lifecycle-step-ejecutivo']
  }

  // Iterate in reverse
  for (let i = bill.stages.length - 1; i >= 0; i--) {
    const stage = bill.stages[i]
    if (stage.phase.includes('Tercer')) {
      return ['Tercer trámite constitucional', 'lifecycle-step-tercer']
    }
    if (stage.phase.includes('Segundo')) {
      return ['Segundo trámite constitucional', 'lifecycle-step-segundo']
    }
    if (stage.phase.includes('Primer')) {
      return ['Primer trámite constitucional', 'lifecycle-step-primer']
    }
  }

  return [bill.status, null]
}

// ---------------------------------------------------------------------------
// Legislator display
// ---------------------------------------------------------------------------

/**
 * Format a legislator's display name as "F. LastName" and initials as "FL".
 * Falls back to raw_name (split on comma) if last_name is missing.
 */
export function legislatorDisplay(
  firstName: string | null,
  lastName: string | null,
  rawName: string,
): [string, string] {
  const last = (lastName != null && lastName !== '')
    ? lastName
    : (rawName.split(',')[0] ?? '').trim()

  const firstChar = firstName != null && firstName.length > 0
    ? firstName[0]
    : null

  const displayName = firstChar != null
    ? `${firstChar.toUpperCase()}. ${last}`
    : last

  const initials = `${firstChar ?? ' '}${last[0] ?? ' '}`.toUpperCase()

  return [displayName, initials]
}

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

/**
 * Build a BCN Ley Chile URL from a law_number string.
 * Strips all non-digit characters from the string; requires ≥4 digits to
 * proceed. Production law numbers contain exactly one digit block, so no
 * disambiguation is needed.
 * Returns null for decretos supremos, resoluciones, or unrecognized formats.
 */
export function bcnLawUrl(lawNumber: string): string | null {
  const s = lawNumber.trim()
  if (s.startsWith('D.S') || s.startsWith('D. S') || s.includes('Resolución')) {
    return null
  }
  const digits = s.replace(/\D/g, '')
  if (digits.length < 4) {
    return null
  }
  return `https://www.bcn.cl/leychile/navegar?idLey=${digits}`
}

/**
 * Build the official tracking URL for a bill on its originating chamber's site.
 * Senado uses the correlative boletín (before the dash); Cámara uses the full NNNNN-SS.
 */
export function billTrackingUrl(boletin: string, originChamber: string): string | null {
  const b = boletin.trim()
  if (b === '') return null

  if (originChamber.includes('Senado')) {
    const correlative = b.split('-')[0] ?? b
    if (correlative === '') return null
    return `https://tramitacion.senado.cl/appsenado/index.php?mo=tramitacion&ac=datosProyecto&nboletin=${correlative}`
  } else {
    return `https://www.camara.cl/legislacion/ProyectosDeLey/tramitacion.aspx?prmBOLETIN=${b}&prmBL=${b}`
  }
}

// ---------------------------------------------------------------------------
// Duration
// ---------------------------------------------------------------------------

/** Duration info: approximate days and human-readable label. */
export interface Duration {
  days: number
  label: string
}

/**
 * Compute the duration between two ISO dates (YYYY-MM-DD).
 * Uses the intentional 365/30-day approximation (y*365 + m*30 + d).
 * Returns null if dates are invalid or end < start.
 */
export function durationBetween(start: string, end: string): Duration | null {
  function parse(s: string): [number, number, number] | null {
    const p = s.split('-')
    if (p.length !== 3) return null
    const y = parseInt(p[0], 10)
    const m = parseInt(p[1], 10)
    const d = parseInt(p[2], 10)
    // Rust uses .parse().ok()? which returns None on failure — mirror that
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null
    return [y, m, d]
  }

  const startParts = parse(start)
  const endParts = parse(end)
  if (startParts == null || endParts == null) return null

  const [y1, m1, d1] = startParts
  const [y2, m2, d2] = endParts
  const days1 = y1 * 365 + m1 * 30 + d1
  const days2 = y2 * 365 + m2 * 30 + d2
  const diff = days2 - days1

  if (diff < 0) return null

  let label: string
  if (diff === 0) {
    label = 'mismo día'
  } else if (diff === 1) {
    label = '1 día'
  } else if (diff < 30) {
    label = `${diff} días`
  } else if (diff < 365) {
    const months = Math.floor(diff / 30)
    label = months === 1 ? '1 mes' : `${months} meses`
  } else {
    const years = Math.floor(diff / 365)
    const remainingMonths = Math.floor((diff % 365) / 30)
    if (remainingMonths === 0) {
      label = years === 1 ? '1 año' : `${years} años`
    } else if (years === 1) {
      label = `1 año y ${remainingMonths} meses`
    } else {
      label = `${years} años y ${remainingMonths} meses`
    }
  }

  return { days: diff, label }
}

/** Emoji and CSS class for published (finished) bills based on how fast. */
export function durationStyleDone(days: number): [string, string] {
  if (days <= 7) return ['⚡', 'bill-duration--fast']
  if (days <= 90) return ['🏃', 'bill-duration--quick']
  if (days <= 365) return ['📋', 'bill-duration--standard']
  if (days <= 730) return ['🐌', 'bill-duration--slow']
  return ['🏔️', 'bill-duration--long']
}

/** Emoji and CSS class for pending (in-progress) bills based on elapsed time. */
export function durationStylePending(days: number): [string, string] {
  if (days <= 90) return ['⏳', 'bill-duration--fresh']
  if (days <= 365) return ['🕐', 'bill-duration--waiting']
  if (days <= 730) return ['😴', 'bill-duration--dormant']
  if (days <= 1460) return ['🧊', 'bill-duration--frozen']
  return ['🪦', 'bill-duration--buried']
}

// ---------------------------------------------------------------------------
// Relative time label
// ---------------------------------------------------------------------------

/**
 * Return a human-readable relative time label and a boolean indicating
 * whether the date is "recent" (diff <= 1 day).
 */
export function relativeTimeLabel(
  iso: string,
  todayYear: number,
  todayMonth: number,
  todayDay: number,
): [string, boolean] {
  const datePart = iso.split('T')[0] ?? iso
  const parts = datePart.split('-')
  if (parts.length !== 3) {
    return [iso, false]
  }

  const year = parseInt(parts[0], 10) || 0
  const month = parseInt(parts[1], 10) || 1
  const day = parseInt(parts[2], 10) || 1

  const dataDays = year * 365 + month * 30 + day
  const todayDays = todayYear * 365 + todayMonth * 30 + todayDay
  const diff = todayDays - dataDays

  let label: string
  if (diff <= 0) {
    label = 'Actualizado hoy'
  } else if (diff === 1) {
    label = 'Actualizado ayer'
  } else if (diff < 7) {
    label = `Hace ${diff} días`
  } else if (diff < 30) {
    const weeks = Math.floor(diff / 7)
    label = weeks === 1 ? 'Hace 1 semana' : `Hace ${weeks} semanas`
  } else {
    const months = Math.floor(diff / 30)
    label = months === 1 ? 'Hace 1 mes' : `Hace ${months} meses`
  }

  return [label, diff <= 1]
}

// ---------------------------------------------------------------------------
// Text normalization
// ---------------------------------------------------------------------------

/** Normalize text for accent-insensitive search. Strips Spanish diacritics and lowercases. */
export function normalizeText(s: string): string {
  return s
    .toLowerCase()
    .replaceAll('á', 'a')
    .replaceAll('é', 'e')
    .replaceAll('í', 'i')
    .replaceAll('ó', 'o')
    .replaceAll('ú', 'u')
    .replaceAll('ñ', 'n')
    .replaceAll('ü', 'u')
}
